import { getCookie, setCookie } from "../actions";
import { AnalyticParams } from "../constants";
import { isParamValid } from "../utils";
import { buildFbc } from "./build-fbc";

type ClickIds = Record<
  | typeof AnalyticParams.CLICK_ID
  | typeof AnalyticParams.FBCLID
  | typeof AnalyticParams.GCLID,
  string | undefined
>;

const CLICK_ID_KEYS = [
  AnalyticParams.FBCLID,
  AnalyticParams.GCLID,
  AnalyticParams.CLICK_ID,
] as const;

const getClickIdFromSearch = (): ClickIds => {
  const searchParams = new URLSearchParams(window.location.search);

  return {
    [AnalyticParams.CLICK_ID]: searchParams.get(AnalyticParams.CLICK_ID) || undefined,
    [AnalyticParams.FBCLID]: searchParams.get(AnalyticParams.FBCLID) || undefined,
    [AnalyticParams.GCLID]: searchParams.get(AnalyticParams.GCLID) || undefined,
  };
};

const transformFbClickId = (fbclid: string) =>
  fbclid.includes("fb.1.") ? fbclid : `fb.1.${Date.now()}.${fbclid}`;

const transformToClickId = (source: ClickIds) => {
  const fbclid = source[AnalyticParams.FBCLID];
  const gclid = source[AnalyticParams.GCLID];
  const clickId = source[AnalyticParams.CLICK_ID];

  if (isParamValid(fbclid)) return transformFbClickId(fbclid);

  if (isParamValid(gclid)) return gclid;

  if (isParamValid(clickId)) return clickId;

  return null;
};

const hasClickIdInSearch = (searchClickIds: ClickIds) =>
  CLICK_ID_KEYS.some((key) => isParamValid(searchClickIds[key]));

// Independent of the click_id cookie below: writes the standard `_fbc` cookie
// Meta Pixel and CAPI actually read, computed before the URL is rewritten so
// it doesn't depend on winning a race with Meta Pixel's own async init.
const syncFbc = (fbclid: string | undefined) => {
  const existingFbc = getCookie(AnalyticParams.FBC) ?? null;

  const computedFbc = buildFbc({
    fbclid: isParamValid(fbclid) ? fbclid : null,
    existingFbc,
    hostname: window.location.hostname,
    now: Date.now(),
  });

  // buildFbc returns existingFbc unchanged when it was already valid - skip
  // the write in that case so we don't reset a cookie Meta Pixel itself may
  // have set with its own (longer) expiry.
  if (computedFbc && computedFbc !== existingFbc) {
    setCookie(AnalyticParams.FBC, computedFbc);
  }
};

export const initializeClickId = (): URLSearchParams => {
  const searchParams = new URLSearchParams(window.location.search);
  const searchClickIds = getClickIdFromSearch();

  syncFbc(searchClickIds[AnalyticParams.FBCLID]);

  const hasSearchClickId = hasClickIdInSearch(searchClickIds);
  const cookieClickId = getCookie(AnalyticParams.CLICK_ID);

  // If in cookie -> set to search params
  if (isParamValid(cookieClickId)) {
    searchParams.delete(AnalyticParams.FBCLID);
    searchParams.delete(AnalyticParams.GCLID);

    searchParams.set(AnalyticParams.CLICK_ID, cookieClickId);

    return searchParams;
  }

  // If in search params but not in cookie -> transform, save to cookie, update search params
  if (hasSearchClickId) {
    const normalizedClickId = transformToClickId(searchClickIds);

    if (normalizedClickId) {
      setCookie(AnalyticParams.CLICK_ID, normalizedClickId);

      searchParams.delete(AnalyticParams.FBCLID);
      searchParams.delete(AnalyticParams.GCLID);

      searchParams.set(AnalyticParams.CLICK_ID, normalizedClickId);
    }
  }

  return searchParams;
};
